/**
 * «Elektron yashil olam» — mahsulot sahifasi (katalog kartochkasidagi «Batafsil»).
 * Tuzilishi: yo'l-yo'riq (tur › toifa), rasmlar galereyasi, asosiy ma'lumot,
 * xarid bloki, tavsif va xususiyatlar, pastda — tavsiya etiladigan mahsulotlar.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useI18n } from "@/shared/i18n";
import { formatMoney, formatNumber } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useFavorites, useGuestCart } from "@/shared/lib/shopStore";
import { Icon } from "@/shared/ui/Icon";
import { ImageGallery } from "@/shared/ui/ImageGallery";

import { MarketCard, stockLabel, useBuy, useMarketCatalog } from "./landing/market";
import { MarketMenu, QuantityInput, sectionPath, useCheckout } from "./landing/MarketPanels";
import type { MarketItem } from "./landing/market";
import { SectionPage } from "./SectionPages";

const RECOMMEND_COUNT = 4;
/** Texnik xususiyatlar jadvalida ko'rsatilmaydigan ichki kalitlar. */
const HIDDEN_SPECS = new Set(["images", "source"]);
const SPEC_LABELS: Record<string, string> = {
  latin_name: "Lotincha nomi",
  age_years: "Yoshi (yil)",
  height: "Balandligi",
  origin: "Yetishtirilgan joy",
  contact_phone: "Aloqa telefoni",
};

/** Botanik pasport kabi uzun matnni sarlavha (`01  Sarlavha`) va xatboshilarga ajratadi. */
function describe(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({ heading: /^\d{2}\s+\S/.test(line), text: line.replace(/^\d{2}\s+/, "") }));
}

function shuffle<T>(list: T[]): T[] {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Avval shu toifadagi, keyin shu turdagi, oxirida qolgan mahsulotlar (o'zi chiqarib tashlanadi). */
function recommend(items: MarketItem[], current: MarketItem, count: number) {
  const others = items.filter((item) => item.key !== current.key && !item.sample);
  const sameCategory = shuffle(others.filter((item) => item.categoryCode === current.categoryCode));
  const sameType = shuffle(
    others.filter((item) => item.typeCode === current.typeCode && item.categoryCode !== current.categoryCode),
  );
  const rest = shuffle(others.filter((item) => item.typeCode !== current.typeCode));
  return {
    related: sameCategory.slice(0, count),
    more: [...sameType, ...rest].slice(0, count),
  };
}

export function MarketProductPage() {
  const { code = "" } = useParams();
  const { locale } = useI18n();
  const catalog = useMarketCatalog(100);
  const buy = useBuy();
  const favorites = useFavorites();
  const cart = useGuestCart();
  const checkout = useCheckout();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const item = useMemo(() => catalog.items.find((row) => row.code === code && !row.sample), [catalog.items, code]);
  const picks = useMemo(() => (item ? recommend(catalog.items, item, RECOMMEND_COUNT) : null), [catalog.items, item]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setAdded(false);
  }, [code]);

  // Boshlang'ich miqdor: savatdagi bo'lsa — shu, bo'lmasa minimal buyurtma.
  useEffect(() => {
    if (item) setQty(cart.quantityOf(item.code) || item.minOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.code]);

  if (!item) {
    return (
      <SectionPage revealKey={`${code}-${catalog.loading}`}>
        <section className="section">
          <div className="container">
            <p className="market-empty">{catalog.loading ? "Yuklanmoqda…" : "Mahsulot topilmadi."}</p>
            <p className="market-more">
              <Link className="btn btn-outline" to={SECTION_PATHS.market}>
                Barcha mahsulotlar
              </Link>
            </p>
          </div>
        </section>
      </SectionPage>
    );
  }

  const inStock = item.available > 0;
  const stock = stockLabel(item, locale);
  const faved = favorites.has(item.code);
  const inCart = cart.quantityOf(item.code);
  const addToCart = () => {
    cart.set(item.code, qty);
    setAdded(true);
  };
  const buyNow = () => {
    cart.set(item.code, qty);
    checkout();
  };
  const typeLink = `${SECTION_PATHS.market}?toifa=${encodeURIComponent(item.categoryCode)}`;
  const categoryLink = typeLink;
  const paragraphs = describe(item.description);
  const specRows = Object.entries(item.specs)
    .filter(([key, value]) => !HIDDEN_SPECS.has(key) && value !== "" && value != null)
    .map(([key, value]) => ({ label: SPEC_LABELS[key] ?? key, value: String(value) }));
  const facts = [
    { label: "Tur", value: item.typeName },
    { label: "Toifa", value: item.categoryName },
    { label: "Birlik", value: item.unit },
    { label: "Minimal buyurtma", value: `${formatNumber(item.minOrder, locale)} ${item.unit}` },
    ...specRows.slice(0, 3),
  ].filter((row) => row.value);

  return (
    <SectionPage revealKey={code}>
      <section className="section section-alt pd" aria-labelledby="pd-title">
        <div className="container">
          <nav className="pd-crumbs" aria-label="Yo'l">
            <Link to={SECTION_PATHS.market}>Elektron yashil olam</Link>
            <span aria-hidden="true">›</span>
            <Link to={typeLink}>{item.typeName}</Link>
            {item.categoryName ? (
              <>
                <span aria-hidden="true">›</span>
                <Link to={categoryLink}>{item.categoryName}</Link>
              </>
            ) : null}
            <span aria-hidden="true">›</span>
            <span aria-current="page">{item.name}</span>
          </nav>

          <MarketMenu />

          <div className="pd-top">
            <div className="pd-gallery">
              {item.images.length ? (
                <ImageGallery images={item.images} alt={item.name} />
              ) : (
                <div className="pd-noimg">
                  <Icon name="leaf" size={64} strokeWidth={1.2} />
                </div>
              )}
            </div>

            <div className="pd-info">
              <h1 id="pd-title">{item.name}</h1>
              <p className="pd-meta">
                <Link to={typeLink} className="badge badge-plain">
                  {item.typeName}
                </Link>
                {item.categoryName ? (
                  <Link to={categoryLink} className="badge badge-plain">
                    {item.categoryName}
                  </Link>
                ) : null}
              </p>
              <hr />
              <p className="pd-price">
                <b className="num">{formatMoney(item.price, locale)}</b>
                <span>/ {item.unit}</span>
              </p>
              <dl className="pd-facts">
                {facts.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <aside className="pd-buybox" aria-label="Xarid">
              <p className="pd-price">
                <b className="num">{formatMoney(item.price, locale)}</b>
                <span>/ {item.unit}</span>
              </p>
              <p className={`pd-stock is-${stock.tone}`}>
                {inStock ? `${stock.text}${stock.tone === "low" ? " — oz qoldi" : ""}` : "Hozircha mavjud emas"}
              </p>
              {inStock ? (
                <>
                  <div className="pd-qty">
                    <span>Miqdor ({item.unit})</span>
                    <QuantityInput value={qty} min={item.minOrder} max={item.available} onChange={setQty} />
                  </div>
                  <p className="pd-total">
                    Jami: <b className="num">{formatMoney(item.price * qty, locale)}</b>
                  </p>
                  <button type="button" className="btn btn-outline btn-block" onClick={addToCart}>
                    <Icon name="cart" size={16} />
                    {inCart ? "Savatni yangilash" : "Savatga qo'shish"}
                  </button>
                  <button type="button" className="btn btn-primary btn-block" onClick={buyNow}>
                    Hoziroq sotib olish
                  </button>
                  {added ? (
                    <p className="pd-added" role="status">
                      <Icon name="check" size={14} />
                      Savatga qo'shildi.{" "}
                      <Link to={sectionPath("cart")}>Savatni ochish</Link>
                    </p>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                className={`btn btn-outline btn-block pd-fav${faved ? " is-on" : ""}`}
                aria-pressed={faved}
                onClick={() => favorites.toggle(item.code)}
              >
                <Icon name="heart" size={16} />
                {faved ? "Sevimlilarda" : "Sevimlilarga qo'shish"}
              </button>
              <dl className="pd-seller">
                <div>
                  <dt>Sotuvchi</dt>
                  <dd>{item.seller}</dd>
                </div>
                <div>
                  <dt>Hudud</dt>
                  <dd>{item.region || "—"}</dd>
                </div>
              </dl>
              <p className="pd-note">
                <Icon name="lock" size={14} />
                Buyurtmani rasmiylashtirishda OneID orqali kirish so'raladi; savatingiz saqlanadi.
              </p>
            </aside>
          </div>

          {paragraphs.length ? (
            <section className="pd-block" aria-labelledby="pd-desc">
              <h2 id="pd-desc">Mahsulot tavsifi</h2>
              <div className="pd-desc">
                {paragraphs.map((row, index) =>
                  row.heading ? <h3 key={index}>{row.text}</h3> : <p key={index}>{row.text}</p>,
                )}
              </div>
            </section>
          ) : null}

          {specRows.length ? (
            <section className="pd-block" aria-labelledby="pd-specs">
              <h2 id="pd-specs">Xususiyatlar</h2>
              <table className="pd-specs">
                <tbody>
                  {specRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {picks?.related.length ? (
            <section className="pd-block" aria-labelledby="pd-related">
              <h2 id="pd-related">Shu toifadagi mahsulotlar{item.categoryName ? ` — ${item.categoryName}` : ""}</h2>
              <div className="market-grid">
                {picks.related.map((row, index) => (
                  <MarketCard key={row.key} item={row} index={index} onBuy={buy} />
                ))}
              </div>
            </section>
          ) : null}

          {picks?.more.length ? (
            <section className="pd-block" aria-labelledby="pd-more">
              <h2 id="pd-more">Sizga ham yoqishi mumkin</h2>
              <div className="market-grid">
                {picks.more.map((row, index) => (
                  <MarketCard key={row.key} item={row} index={index} onBuy={buy} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </SectionPage>
  );
}
